import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

import type { MrpSkuHoverHintFields } from "@/lib/mrp";

import { MrpSkuHoverPeek } from "@/components/mrp/MrpSkuHoverPeek";

import { kittingDestinationAccentClass } from "./kittingCartModel";
import { sortKittingPickLinesByCartLetter, type KittingCartMark } from "./kittingCartMarks";
import { kittingDestinationDisplayLines, kittingPickLineQuantities } from "./kittingScanModel";
import type { KittingPickLine } from "./kittingPickModel";
import { kittingPickUnlabeledQuantity, type KittingPickCartEntry } from "./kittingPickCartModel";
import type { KittingPickNumpadCorner } from "@/pages/prepack/prepackTransferKioskLayout";

function kioskSkuDescriptionLine(hint: MrpSkuHoverHintFields | undefined, fallback: string): string {
  const parts = [hint?.description?.trim(), hint?.variant?.trim()].filter(
    (part): part is string => part != null && part !== "",
  );
  return parts.length > 0 ? parts.join(" ") : fallback;
}

export function KittingPickHScrollStrip({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: ReactNode;
}): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateOverflow = useCallback(() => {
    const node = scrollerRef.current;
    if (node == null) return;
    const max = Math.max(0, node.scrollWidth - node.clientWidth);
    setCanScrollLeft(node.scrollLeft > 4);
    setCanScrollRight(node.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    const node = scrollerRef.current;
    if (node == null) return;
    updateOverflow();
    node.addEventListener("scroll", updateOverflow, { passive: true });
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(node);
    if (node.firstElementChild != null) {
      observer.observe(node.firstElementChild);
    }
    const mutationObserver = new MutationObserver(updateOverflow);
    mutationObserver.observe(node, { childList: true, subtree: true });
    return () => {
      node.removeEventListener("scroll", updateOverflow);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [updateOverflow, children]);

  function handleWheel(event: ReactWheelEvent<HTMLDivElement>): void {
    const node = scrollerRef.current;
    if (
      node == null
      || node.scrollWidth <= node.clientWidth
      || Math.abs(event.deltaX) >= Math.abs(event.deltaY)
    ) {
      return;
    }
    event.preventDefault();
    node.scrollLeft += event.deltaY;
  }

  function scrollByPage(direction: -1 | 1): void {
    const node = scrollerRef.current;
    if (node == null) return;
    node.scrollBy({ left: direction * Math.max(160, node.clientWidth * 0.7), behavior: "smooth" });
  }

  return (
    <div className="relative min-w-0">
      <div
        ref={scrollerRef}
        aria-label={ariaLabel}
        onWheel={handleWheel}
        className={cn(
          "flex w-full min-w-0 gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x pb-1",
          "[-webkit-overflow-scrolling:touch] [scrollbar-width:thin]",
          "[&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-400",
        )}
      >
        {children}
      </div>
      {canScrollLeft ? (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent"
            aria-hidden
          />
          <button
            type="button"
            aria-label="Scroll left"
            className="absolute left-0 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm"
            onClick={() => scrollByPage(-1)}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
        </>
      ) : null}
      {canScrollRight ? (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent"
            aria-hidden
          />
          <button
            type="button"
            aria-label="Scroll right"
            className="absolute right-0 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm"
            onClick={() => scrollByPage(1)}
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
        </>
      ) : null}
    </div>
  );
}

export function KittingPickSkuTile({
  sku,
  storageLocation,
  hint,
  selected,
  disabled,
  onSelect,
}: {
  sku: string;
  storageLocation: string;
  hint: MrpSkuHoverHintFields | undefined;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex h-full w-[7.75rem] max-w-[7.75rem] min-w-0 shrink-0 flex-col self-stretch overflow-hidden rounded-md border-2 px-1.5 py-1 text-left transition",
        "xl:min-h-[6.75rem] xl:w-[13.5rem] xl:max-w-[13.5rem] xl:rounded-lg xl:p-2",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
        selected
          ? "border-brand-500 bg-brand-50 shadow-sm"
          : "border-slate-300 bg-white hover:border-slate-400 active:bg-slate-50",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <p className="truncate text-sm font-bold leading-tight text-slate-900 xl:line-clamp-2 xl:text-xl">
        {storageLocation}
      </p>
      <p className="mt-0.5 truncate text-xs font-semibold leading-snug text-slate-900 xl:mt-1 xl:line-clamp-2 xl:text-sm">
        {sku}
      </p>
      <p className="mt-0.5 truncate text-[10px] leading-snug text-slate-600 xl:mt-1 xl:line-clamp-2 xl:flex-1 xl:text-[11px] xl:whitespace-normal">
        {kioskSkuDescriptionLine(hint, "—")}
      </p>
    </button>
  );
}

const KITTING_PICK_NUMPAD_CORNERS: readonly {
  id: KittingPickNumpadCorner;
  label: string;
}[] = [
  { id: "top-left", label: "Numpad top left" },
  { id: "top-right", label: "Numpad top right" },
  { id: "bottom-left", label: "Numpad bottom left" },
  { id: "bottom-right", label: "Numpad bottom right" },
];

export function KittingPickNumpadCornerToggle({
  corner,
  onCornerChange,
}: {
  corner: KittingPickNumpadCorner;
  onCornerChange: (corner: KittingPickNumpadCorner) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Numpad corner"
      className="grid shrink-0 grid-cols-2 gap-0.5 rounded-md border border-slate-200 bg-white p-0.5"
    >
      {KITTING_PICK_NUMPAD_CORNERS.map((item) => {
        const selected = corner === item.id;
        return (
          <button
            key={item.id}
            type="button"
            aria-label={item.label}
            aria-pressed={selected}
            className={cn(
              "inline-flex h-12 w-12 items-center justify-center rounded-sm",
              selected ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50",
            )}
            onClick={() => onCornerChange(item.id)}
          >
            <span className="grid h-5 w-5 grid-cols-2 grid-rows-2 gap-px" aria-hidden>
              {KITTING_PICK_NUMPAD_CORNERS.map((cell) => (
                <span
                  key={cell.id}
                  className={cn(
                    "rounded-[1px]",
                    cell.id === item.id
                      ? selected
                        ? "bg-white"
                        : "bg-brand-600"
                      : selected
                        ? "bg-brand-500"
                        : "bg-slate-300",
                  )}
                />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Fixed height for the desktop kiosk job-line picker row. */
export const KITTING_PICK_LINE_PICKER_ROW_HEIGHT_CLASS = "h-[6.75rem]";

const KITTING_PICK_LINE_PICKER_TILE_SHELL_CLASS =
  "flex min-h-12 w-[10.5rem] shrink-0 flex-row items-center gap-2 overflow-hidden rounded-md border-2 px-1.5 py-1 xl:h-[6.75rem] xl:min-h-0 xl:w-[11.5rem] xl:flex-col xl:items-stretch xl:gap-0 xl:rounded-lg xl:p-1.5";

/** Quantity strip for job-line tiles. Labels stay visible so Need / Alloc / Left stay readable. */
export function KittingPickLineQuantityStrip({ line }: { line: KittingPickLine }): JSX.Element {
  const { needed, allocated, left } = kittingPickLineQuantities(line);
  return (
    <div
      className="min-w-0 flex-1 text-center xl:flex-none xl:border-t xl:border-slate-200 xl:pt-0.5 xl:text-[10px]"
      aria-label={`Need ${needed}, allocated ${allocated}, remaining ${left}`}
    >
      <div className="grid grid-cols-3 justify-items-center gap-1 text-[10px] font-semibold uppercase tracking-normal text-slate-500 xl:tracking-wide">
        <p>Need</p>
        <p>Alloc</p>
        <p>Left</p>
      </div>
      <div className="grid grid-cols-3 items-baseline justify-items-center gap-1 tabular-nums">
        <p className="text-sm font-semibold text-slate-700">{needed}</p>
        <p className="text-sm font-semibold text-slate-700">{allocated}</p>
        <p
          className={cn(
            "text-base font-bold leading-none xl:text-lg",
            left > 0 ? "text-amber-800" : "text-emerald-700",
          )}
        >
          {left}
        </p>
      </div>
    </div>
  );
}

export function KittingPickLinePickerRow({
  candidateLines,
  marksBySlotId,
  selectedLineId,
  onSelectLine,
  placeholder = "Select a component above",
  alignEnd = false,
}: {
  candidateLines: readonly KittingPickLine[];
  marksBySlotId: ReadonlyMap<string, KittingCartMark>;
  selectedLineId: string | null;
  onSelectLine: (line: KittingPickLine) => void;
  placeholder?: string;
  /** When true, right-align tiles (numpad on the right). */
  alignEnd?: boolean;
}): JSX.Element {
  const displayLines = sortKittingPickLinesByCartLetter(candidateLines, marksBySlotId);

  return (
    <div className="min-w-0">
      <KittingPickHScrollStrip ariaLabel="Job line selection">
        <div
          className={cn(
            "flex w-max min-w-full items-center gap-1.5",
            alignEnd ? "xl:justify-end" : "xl:justify-start",
            "xl:h-[6.75rem]",
          )}
        >
          {displayLines.length > 0 ? (
            displayLines.map((line) => (
              <KittingPickLinePickerTile
                key={line.id}
                line={line}
                mark={marksBySlotId.get(line.destinationSlotId) ?? null}
                selected={selectedLineId === line.id}
                onSelect={() => onSelectLine(line)}
              />
            ))
          ) : (
            <div
              className={cn(
                KITTING_PICK_LINE_PICKER_TILE_SHELL_CLASS,
                "items-center justify-center border-dashed border-slate-200 bg-slate-50",
              )}
            >
              <p className="line-clamp-2 px-1 text-center text-sm leading-snug text-slate-500">{placeholder}</p>
            </div>
          )}
        </div>
      </KittingPickHScrollStrip>
    </div>
  );
}

export function KittingPickLinePickerTile({
  line,
  mark,
  selected,
  onSelect,
}: {
  line: KittingPickLine;
  mark?: KittingCartMark | null;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const accent = kittingDestinationAccentClass(
    line.job.kind === "batch"
      ? "batch"
      : line.job.kind === "transfer_order"
        ? "transfer_order"
        : "work_order",
  );
  const destinationLines = kittingDestinationDisplayLines(line.destinationLabel);
  const { needed, allocated, left } = kittingPickLineQuantities(line);
  const full = left <= 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={full}
      aria-pressed={selected}
      className={cn(
        KITTING_PICK_LINE_PICKER_TILE_SHELL_CLASS,
        "min-h-0 text-left transition",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
        full
          ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
          : selected
            ? "border-brand-500 bg-brand-50 shadow-sm"
            : "border-slate-300 bg-white hover:border-slate-400 active:bg-slate-50",
      )}
      aria-label={
        full
          ? `${destinationLines.primary}, full, need ${needed}, allocated ${allocated}, remaining ${left}`
          : `${destinationLines.primary}, need ${needed}, allocated ${allocated}, remaining ${left}`
      }
    >
      <span
        className={cn(
          "flex min-h-0 w-8 shrink-0 items-stretch overflow-hidden rounded-md border border-slate-200 xl:w-auto xl:flex-1",
          mark == null && "border-l-4",
          mark == null && accent,
        )}
      >
        {mark != null ? (
          <span
            className={cn(
              "inline-flex w-8 shrink-0 items-center justify-center self-stretch text-lg font-bold leading-none xl:w-9 xl:text-xl",
              mark.badgeClass,
            )}
            aria-hidden
          >
            {mark.letter}
          </span>
        ) : null}
        <span
          className={cn(
            "hidden min-w-0 flex-col justify-center leading-tight xl:flex",
            mark != null && "px-1.5 py-0.5",
          )}
        >
          <span className="truncate text-sm font-semibold text-slate-900">{destinationLines.primary}</span>
          {destinationLines.secondary != null ? (
            <span className="truncate text-[10px] font-medium text-slate-700">
              {destinationLines.secondary}
            </span>
          ) : null}
        </span>
      </span>

      <KittingPickLineQuantityStrip line={line} />
    </button>
  );
}

export function KittingPickCartPanel({
  entries,
  marksBySlotId,
  hoverHints,
  disabled,
  returningEntryId,
  onReturnEntry,
  onReturnContainer,
}: {
  entries: readonly KittingPickCartEntry[];
  marksBySlotId: ReadonlyMap<string, KittingCartMark>;
  hoverHints?: Readonly<Record<string, MrpSkuHoverHintFields>>;
  disabled?: boolean;
  returningEntryId?: string | null;
  onReturnEntry?: (entry: KittingPickCartEntry) => void;
  onReturnContainer?: (entry: KittingPickCartEntry, container: KittingPickCartEntry["pickedContainers"][number]) => void;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <p className="px-1 py-4 text-center text-sm text-slate-500">
        Allocated picks for these cart bins appear here after quantity is picked.
      </p>
    );
  }

  const digitCount = Math.max(...entries.map((entry) => String(entry.quantity).length), 1);

  return (
    <ul className="no-scrollbar flex min-h-0 flex-1 touch-pan-y flex-col gap-1.5 overflow-y-auto [-webkit-overflow-scrolling:touch]">
      {entries.map((entry) => {
        const mark = marksBySlotId.get(entry.destinationSlotId) ?? null;
        const containers = entry.pickedContainers ?? [];
        const unlabeledQuantity = kittingPickUnlabeledQuantity(entry);
        const isReturningSku = returningEntryId === entry.id;
        return (
          <li
            key={entry.id}
            className="flex min-w-0 flex-col gap-1 rounded-md border border-slate-200 bg-white px-2 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              <p
                className="shrink-0 text-right text-3xl font-bold tabular-nums leading-none text-slate-900"
                style={{ width: `${String(digitCount)}ch` }}
                aria-label={`Quantity ${String(entry.quantity)}`}
              >
                {entry.quantity}
              </p>
              <div className="min-w-0 flex-1">
                <MrpSkuHoverPeek
                  sku={entry.sku}
                  hint={hoverHints?.[entry.sku.trim()]}
                  className="block truncate text-sm font-semibold text-slate-900 no-underline decoration-transparent"
                />
                {(entry.unpickedContainerShortCodes ?? []).length > 0 ? (
                  <p className="truncate font-mono text-xs text-slate-500">
                    Not picked: {(entry.unpickedContainerShortCodes ?? []).join(", ")}
                  </p>
                ) : null}
              </div>
              {mark != null ? (
                <span
                  className={cn(
                    "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                    mark.badgeClass,
                  )}
                  aria-label={`Cart bin ${mark.letter}`}
                >
                  {mark.letter}
                </span>
              ) : null}
              {onReturnEntry != null && unlabeledQuantity > 0 && containers.length === 0 ? (
                <button
                  type="button"
                  disabled={disabled || isReturningSku}
                  onClick={() => onReturnEntry(entry)}
                  aria-label={isReturningSku ? "Returning" : "Return"}
                  className={cn(
                    "inline-flex h-12 min-w-[4.5rem] shrink-0 touch-manipulation items-center justify-center rounded-md border border-slate-300 px-3 text-xs font-semibold text-slate-700",
                    "hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {isReturningSku ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                  ) : (
                    "Return"
                  )}
                </button>
              ) : null}
            </div>
            {containers.length > 0 ? (
              <ul className="flex flex-col gap-1 pl-1">
                {unlabeledQuantity > 0 && containers.length > 0 && onReturnEntry != null ? (
                  <li className="flex min-w-0 items-center gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1">
                    <p className="w-16 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-900">
                      {unlabeledQuantity}
                    </p>
                    <p className="min-w-0 flex-1 truncate text-xs text-slate-600">No container</p>
                    <button
                      type="button"
                      disabled={disabled || isReturningSku}
                      onClick={() => onReturnEntry(entry)}
                      aria-label={isReturningSku ? "Returning" : "Return quantity with no container"}
                      className={cn(
                        "inline-flex h-10 min-w-[4.5rem] shrink-0 touch-manipulation items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700",
                        "hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                      )}
                    >
                      {isReturningSku ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                      ) : (
                        "Return"
                      )}
                    </button>
                  </li>
                ) : null}
                {containers.map((container) => {
                  const returnKey = `${entry.id}::${container.hex || container.shortCode}`;
                  const isReturning = returningEntryId === returnKey;
                  return (
                    <li
                      key={returnKey}
                      className="flex min-w-0 items-center gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1"
                    >
                      <p className="w-16 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-900">
                        {container.quantity > 0 ? container.quantity : ""}
                      </p>
                      <p className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-slate-800">
                        {container.shortCode}
                      </p>
                      {onReturnContainer != null ? (
                        <button
                          type="button"
                          disabled={disabled || isReturning}
                          onClick={() => onReturnContainer(entry, container)}
                          aria-label={isReturning ? "Returning container" : `Return ${container.shortCode}`}
                          className={cn(
                            "inline-flex h-10 min-w-[4.5rem] shrink-0 touch-manipulation items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700",
                            "hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800",
                            "disabled:cursor-not-allowed disabled:opacity-50",
                          )}
                        >
                          {isReturning ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                          ) : (
                            "Return"
                          )}
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
